/**
 * Samadhan Center — Admin Panel
 * - Gallery upload (URL) — supports unlimited images
 * - Transaction ledger
 * - Reservoir & Admin wallet stats
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, Sparkles } from 'lucide-react';
import api from '../../utils/api';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

export default function SamadhanAdmin() {
  const [dash, setDash] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [txns, setTxns] = useState([]);
  const [form, setForm] = useState({ image_url: '', title: '', caption: '', zone: 'general', sort_order: 100 });
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({ cert_template_id: 'appreciation', tier_templates: {} });
  const [customTpls, setCustomTpls] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [tplForm, setTplForm] = useState({ label: '', bg_url: '', signature_name: 'Anil Tahalani', signature_title: 'THE LIFE MASTER & FOUNDER', signature_url: '', unique_prefix: 'SAMADHAN', accent: '#0891b2', text_dark: '#0f172a' });
  const load = useCallback(async () => {
    try {
      const [d, g, t, s, ct, rd] = await Promise.all([
        api.get('/admin/samadhan/dashboard'),
        api.get('/samadhan/gallery'),
        api.get('/admin/samadhan/transactions?limit=200'),
        api.get('/admin/samadhan/settings').catch(() => ({ data: { settings: { cert_template_id: 'appreciation' } } })),
        api.get('/admin/samadhan/templates').catch(() => ({ data: { custom_templates: [] } })),
        api.get('/admin/samadhan/redemptions?status=all&limit=100').catch(() => ({ data: { redemptions: [] } })),
      ]);
      setDash(d.data); setGallery(g.data.images || []); setTxns(t.data.purchases || []);
      setSettings(s.data.settings || { cert_template_id: 'appreciation' });
      setCustomTpls(ct.data.custom_templates || []);
      setRedemptions(rd.data.redemptions || []);
    } catch (e) { toast.error('Load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const addImg = async () => {
    if (!form.image_url.trim()) return toast.error('URL ya file zaroori hai');
    const url = form.image_url.trim();
    // Accept: http(s) URLs, data: URIs (from file upload)
    const isDataUri = url.startsWith('data:');
    if (!isDataUri && !/^https?:\/\//i.test(url)) {
      return toast.error('URL me https:// ya http:// zaroori hai (ya file upload karo)');
    }
    // Detect video: extension, YouTube/Vimeo, or data:video/*
    const isVideo = /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(url)
                 || /youtube\.com|youtu\.be|vimeo\.com/i.test(url)
                 || /^data:video\//i.test(url);
    setBusy(true);
    try {
      // Only pre-validate IMAGE URLs with Image() — videos skip this test (Image() cannot load videos)
      if (!isVideo && !isDataUri) {
        await new Promise((resolve, reject) => {
          const testImg = new Image();
          testImg.onload = resolve;
          testImg.onerror = () => reject(new Error('URL se image load nahi ho rahi — check karo public link hai ya nahi'));
          setTimeout(() => reject(new Error('URL timeout — 8 seconds me load nahi hui')), 8000);
          testImg.src = url;
        });
      }
      await api.post('/admin/samadhan/gallery', form);
      toast.success(isVideo ? '✅ Video added' : '✅ Image added & verified');
      setForm({ image_url: '', title: '', caption: '', zone: 'general', sort_order: 100 });
      load();
    } catch (e) {
      toast.error(e.message || e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  // 📤 File upload — small images → base64, videos & large files → multipart POST to /admin/samadhan/upload-file
  const uploadGalleryFile = async (file) => {
    if (!file) return;
    const isImg = file.type.startsWith('image/');
    const isVid = file.type.startsWith('video/');
    if (!isImg && !isVid) return toast.error('Sirf image/video files supported hain');
    const maxMB = isVid ? 50 : 8;
    if (file.size > maxMB * 1024 * 1024) return toast.error(`File > ${maxMB} MB — chhoti file try karo`);

    // Videos ALWAYS use server upload (avoids massive base64 in DB). Also big images (>1 MB).
    if (isVid || file.size > 1 * 1024 * 1024) {
      try {
        setBusy(true);
        const fd = new FormData();
        fd.append('file', file);
        const r = await api.post('/admin/samadhan/upload-file', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        // Absolute URL (backend origin) for external playability, or leave relative — both work in-app
        const backend = process.env.REACT_APP_BACKEND_URL || '';
        const fullUrl = backend ? `${backend}${r.data.url}` : r.data.url;
        setForm((f) => ({ ...f, image_url: fullUrl }));
        toast.success(`${isVid ? 'Video' : 'Image'} uploaded (${(r.data.size_bytes / 1024 / 1024).toFixed(1)} MB) — ab "Add" dabao`);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Upload failed');
      } finally { setBusy(false); }
      return;
    }
    // Small images → base64 (fast, no server round-trip)
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, image_url: ev.target.result }));
    reader.readAsDataURL(file);
    toast.success('Image loaded — ab "Add" dabao');
  };
  const delImg = async (id) => {
    if (!window.confirm('Delete image?')) return;
    try { await api.delete(`/admin/samadhan/gallery/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const seedSamples = async () => {
    if (!window.confirm('15 curated sample images (wellness, entertainment, leisure, general) ek click mein add karein?')) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/samadhan/gallery/seed-samples');
      toast.success(`✅ ${r.data.inserted} sample images added (Total active: ${r.data.total_active})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 text-white bg-gradient-to-br from-slate-950 to-indigo-950/40">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs text-amber-400 font-bold uppercase tracking-widest">Admin · Samadhan Center</div>
            <h1 className="text-2xl font-black">🏛️ The Dream Project — Control Panel</h1>
          </div>
          <button onClick={load} className="px-3 py-1.5 rounded-lg bg-white/10 text-slate-200 flex items-center gap-1 text-xs"><RefreshCw className="w-3 h-3" /> Reload</button>
          <button
            onClick={async () => {
              if (!window.confirm('Seed 5 dummy FOUNDERS for testing?\n\nYe test data hai — real wallet debit nahi hoga. Baad mein "Purge Test" se clean kar sakte ho.')) return;
              try { const r = await api.post('/admin/samadhan/seed-founders?count=5'); toast.success(`✅ ${r.data.created} test Founders created`); load(); }
              catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
            }}
            data-testid="samadhan-seed-founders"
            className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold text-xs">
            🌱 Seed 5 Test Founders
          </button>
          <button
            onClick={async () => {
              if (!window.confirm('Purge ALL test Founders?\n\nSirf is_test:true wale rows delete honge. Real donations safe hain.')) return;
              try { const r = await api.delete('/admin/samadhan/seed-founders'); toast.success(`🗑️ Deleted ${r.data.deleted} test rows`); load(); }
              catch (e) { toast.error(e.response?.data?.detail || 'Purge failed'); }
            }}
            data-testid="samadhan-purge-founders"
            className="px-3 py-1.5 rounded-lg bg-red-600/80 text-white font-bold text-xs">
            🧹 Purge Test Data
          </button>
        </div>

        {/* KPIs */}
        {dash && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="Reservoir Fund" val={`${fmt(dash.stats.reservoir_balance)} AT`} sub={`${dash.stats.progress_pct}% of 50 Cr`} cls="from-emerald-500/20 to-teal-500/20 border-emerald-400/30" />
            <Card label="Admin Wallet"  val={`${fmt(dash.stats.admin_balance)} AT`} sub="20% share" cls="from-amber-500/20 to-orange-500/20 border-amber-400/30" />
            <Card label="Total Purchases" val={String(dash.stats.total_purchases)} sub={`${dash.stats.unique_donors} unique donors`} cls="from-fuchsia-500/20 to-pink-500/20 border-fuchsia-400/30" />
            <Card label="Total Raised" val={`${fmt(dash.stats.total_raised)} AT`} sub="Reservoir + Admin" cls="from-indigo-500/20 to-purple-500/20 border-indigo-400/30" />
          </div>
        )}

        {/* Redemptions */}
        <div className="rounded-2xl border-2 border-orange-400/50 bg-orange-500/5 p-4" data-testid="admin-redemptions-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-sm font-black text-orange-300 flex items-center gap-2">🎁 Certificate Redemptions ({redemptions.length})</div>
              <div className="text-[10px] text-slate-400">Users who redeemed their certificates — reach out within 24-48 hrs</div>
            </div>
          </div>
          {redemptions.length === 0 ? (
            <div className="text-xs text-slate-500 text-center py-4 italic">Koi redemption request nahi hai abhi.</div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {redemptions.map((r) => (
                <div key={r.cert_id} className={`rounded-xl border-2 p-3 ${r.redeem_status === 'fulfilled' ? 'border-emerald-400/40 bg-emerald-500/5' : 'border-orange-400/40 bg-orange-500/5'}`} data-testid={`redeem-row-${r.cert_id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-mono text-xs text-amber-300 font-bold">{r.cert_id}</span>
                        {r.nameplate && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-400/40">🏛️ {r.nameplate}</span>}
                        <span className="text-[10px] text-fuchsia-300 font-mono">{fmt(r.amount)} AT</span>
                        <span className="text-[10px] text-slate-400">{r.pack_label}</span>
                        {r.redeem_status === 'fulfilled' ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 font-black">✅ FULFILLED</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/30 text-orange-200 border border-orange-400/40 font-black animate-pulse">⏳ PENDING</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-300">
                        <b className="text-white">{r.redeem_details?.full_name}</b>
                        {' · '}<a href={`tel:${r.redeem_details?.phone}`} className="text-emerald-300 font-mono">📞 {r.redeem_details?.phone}</a>
                        {r.redeem_details?.preferred_date && <> {' · '}📅 <span className="text-sky-300">{r.redeem_details.preferred_date}</span></>}
                      </div>
                      {r.redeem_details?.address && <div className="text-[10px] text-slate-400 mt-0.5">📍 {r.redeem_details.address}</div>}
                      {r.redeem_details?.notes && <div className="text-[10px] text-yellow-200/90 mt-0.5 italic">📝 {r.redeem_details.notes}</div>}
                      <div className="text-[9px] text-slate-500 mt-1">
                        Original donor: {r.full_name || r.username} · Redeemed: {r.redeemed_at ? new Date(r.redeemed_at).toLocaleString('en-IN') : '—'}
                      </div>
                    </div>
                    {r.redeem_status !== 'fulfilled' && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Mark ${r.cert_id} as FULFILLED?`)) return;
                          try { await api.post(`/admin/samadhan/redemptions/${r.cert_id}/fulfill`); toast.success('✅ Marked fulfilled'); load(); }
                          catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                        }}
                        data-testid={`redeem-fulfill-${r.cert_id}`}
                        className="px-2.5 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-[10px] font-black flex-shrink-0">
                        ✅ Mark Fulfilled
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Certificate Template Selector */}
        <div className="rounded-2xl border border-amber-400/40 bg-amber-500/5 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="text-sm font-bold text-amber-300">🏆 Certificate Template</div>
            <span className="text-[10px] text-slate-400">— User ko donation ke baad ye design milega</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { id: 'appreciation', label: '🏆 Certificate of Appreciation', desc: 'Blue/teal ornate frame · Lotus logo · (Recommended)' },
              { id: 'gold_divine',   label: '🕉️ Divine Gold Ornate',           desc: 'Traditional gold gradient · Bold serif' },
              { id: 'royal_purple',  label: '👑 Royal Purple (Classic)',        desc: 'Original design · Purple/fuchsia gradient' },
              ...customTpls.map((c) => ({ id: c.id, label: c.label, desc: `Custom · ${c.unique_prefix || 'SAMADHAN'} · ${c.signature_name || 'Anil Tahalani'}`, custom: true })),
            ].map((t) => (
              <div key={t.id} className="relative">
                <button
                  onClick={async () => {
                    try {
                      await api.post('/admin/samadhan/settings', { cert_template_id: t.id });
                      setSettings({ ...settings, cert_template_id: t.id });
                      toast.success(`✅ Template set to: ${t.label}`);
                    } catch { toast.error('Failed to save template'); }
                  }}
                  data-testid={`cert-template-${t.id}`}
                  className={`w-full p-3 rounded-xl text-left transition-all border-2 ${settings.cert_template_id === t.id ? 'border-amber-400 bg-amber-500/20 ring-2 ring-amber-400/40' : 'border-white/10 bg-white/5 hover:border-amber-400/50 hover:bg-white/10'}`}>
                  <div className="text-xs font-black text-white pr-6">{t.label}</div>
                  <div className="text-[10px] text-slate-300 mt-1">{t.desc}</div>
                  {settings.cert_template_id === t.id && <div className="text-[9px] text-amber-300 font-bold mt-1 uppercase tracking-widest">✓ ACTIVE</div>}
                </button>
                {t.custom && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Delete custom template "${t.label}"?`)) return;
                      try { await api.delete(`/admin/samadhan/templates/${t.id}`); toast.success('Deleted'); load(); }
                      catch { toast.error('Delete failed'); }
                    }}
                    className="absolute top-1 right-1 p-1 rounded bg-red-500/30 hover:bg-red-500/60 text-red-200"
                    data-testid={`cert-template-del-${t.id}`}
                    title="Delete custom template">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Tier → Template Auto Mapping */}
          <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/5 p-3">
            <div className="text-xs font-bold text-emerald-300 mb-1">🎯 Tier-based Auto Template Mapping</div>
            <div className="text-[10px] text-slate-400 mb-2">
              Har donation tier ke liye alag template auto-assign karo. Agar tier ka mapping set nahi hai toh default <b className="text-amber-300">{settings.cert_template_id}</b> use hoga.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {[
                { key: 'DIVINE BASE BUILDER', icon: '🕉️', color: 'from-violet-500 to-amber-400' },
                { key: 'SUPER BASE BUILDER',  icon: '👑', color: 'from-indigo-500 to-violet-600' },
                { key: 'VVIP BASE BUILDER',   icon: '💎', color: 'from-purple-500 to-fuchsia-600' },
                { key: 'VIP BASE BUILDER',    icon: '⭐', color: 'from-fuchsia-500 to-pink-500' },
                { key: 'BASE BUILDER',        icon: '🏛️', color: 'from-amber-500 to-orange-500' },
              ].map((tier) => {
                const cur = (settings.tier_templates || {})[tier.key] || '';
                const options = [
                  { id: '', label: `— Use default (${settings.cert_template_id})` },
                  { id: 'appreciation', label: '🏆 Appreciation (Blue/Teal)' },
                  { id: 'gold_divine',   label: '🕉️ Divine Gold' },
                  { id: 'royal_purple',  label: '👑 Royal Purple' },
                  ...customTpls.map((c) => ({ id: c.id, label: `🎨 ${c.label}` })),
                ];
                return (
                  <div key={tier.key} className={`p-2 rounded-lg bg-gradient-to-br ${tier.color} bg-opacity-10 border border-white/10`}>
                    <div className="text-[10px] font-black text-white mb-1">{tier.icon} {tier.key}</div>
                    <select
                      value={cur}
                      onChange={async (e) => {
                        const val = e.target.value;
                        const newMap = { ...(settings.tier_templates || {}) };
                        if (val) newMap[tier.key] = val; else delete newMap[tier.key];
                        try {
                          await api.post('/admin/samadhan/settings', { tier_templates: newMap });
                          setSettings({ ...settings, tier_templates: newMap });
                          toast.success(`✅ ${tier.key} → ${val || 'default'}`);
                        } catch { toast.error('Failed to save'); }
                      }}
                      data-testid={`tier-tpl-${tier.key.replace(/\s+/g, '-').toLowerCase()}`}
                      className="w-full bg-slate-900 border border-white/10 rounded px-2 py-1 text-[10px] text-white">
                      {options.map((o) => <option key={o.id} value={o.id} className="bg-slate-900">{o.label}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
            <div className="text-[9px] text-slate-400 mt-2 italic">
              💡 Recommended: <b>DIVINE / SUPER / VVIP</b> → "Divine Gold" · <b>VIP / BASE BUILDER</b> → "Appreciation" · Regular donors → default
            </div>
          </div>

          {/* Custom Template Builder */}
          <details className="rounded-lg border border-fuchsia-400/30 bg-fuchsia-500/5 overflow-hidden">
            <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-fuchsia-300 hover:bg-fuchsia-500/10">
              🎨 Custom Template Builder (Add your own design)
            </summary>
            <div className="p-3 grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs">
              {/* Form */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input placeholder="Template Label (e.g. My Diwali Gold)" value={tplForm.label} onChange={(e) => setTplForm({ ...tplForm, label: e.target.value })} data-testid="cert-tpl-label" className="sm:col-span-2 bg-white/5 border border-white/10 rounded px-2 py-1.5" />
                <input placeholder="Background Image URL (jpg/png/webp)" value={tplForm.bg_url} onChange={(e) => setTplForm({ ...tplForm, bg_url: e.target.value })} data-testid="cert-tpl-bg" className="sm:col-span-2 bg-white/5 border border-white/10 rounded px-2 py-1.5" />
                <input placeholder="Signature Name (e.g. Anil Tahalani)" value={tplForm.signature_name} onChange={(e) => setTplForm({ ...tplForm, signature_name: e.target.value })} data-testid="cert-tpl-sig-name" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
                <input placeholder="Signature Title" value={tplForm.signature_title} onChange={(e) => setTplForm({ ...tplForm, signature_title: e.target.value })} data-testid="cert-tpl-sig-title" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
                <input placeholder="Signature PNG URL (optional)" value={tplForm.signature_url} onChange={(e) => setTplForm({ ...tplForm, signature_url: e.target.value })} data-testid="cert-tpl-sig-url" className="sm:col-span-2 bg-white/5 border border-white/10 rounded px-2 py-1.5" />
                <input placeholder="Unique Prefix (e.g. SAMADHAN)" value={tplForm.unique_prefix} onChange={(e) => setTplForm({ ...tplForm, unique_prefix: e.target.value })} data-testid="cert-tpl-prefix" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-slate-400">Accent:</label>
                  <input type="color" value={tplForm.accent} onChange={(e) => setTplForm({ ...tplForm, accent: e.target.value })} className="h-8 w-14 rounded bg-transparent border border-white/10" />
                  <label className="text-[10px] text-slate-400">Text:</label>
                  <input type="color" value={tplForm.text_dark} onChange={(e) => setTplForm({ ...tplForm, text_dark: e.target.value })} className="h-8 w-14 rounded bg-transparent border border-white/10" />
                </div>
                <button
                  onClick={async () => {
                    if (!tplForm.label.trim()) return toast.error('Template label zaroori hai');
                    try {
                      await api.post('/admin/samadhan/templates', tplForm);
                      toast.success(`✅ Custom template "${tplForm.label}" added`);
                      setTplForm({ label: '', bg_url: '', signature_name: 'Anil Tahalani', signature_title: 'THE LIFE MASTER & FOUNDER', signature_url: '', unique_prefix: 'SAMADHAN', accent: '#0891b2', text_dark: '#0f172a' });
                      load();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
                  }}
                  data-testid="cert-tpl-save"
                  className="sm:col-span-2 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-bold text-xs flex items-center justify-center gap-1">
                  <Plus className="w-3 h-3" /> Save Custom Template
                </button>
              </div>

              {/* Live Preview */}
              <div className="space-y-1.5">
                <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">🔍 Live Preview</div>
                <div
                  data-testid="cert-tpl-preview"
                  className="relative rounded-lg overflow-hidden shadow-lg border-2"
                  style={{
                    aspectRatio: '1.7 / 1',
                    background: tplForm.bg_url
                      ? `url("${tplForm.bg_url}") center/cover no-repeat`
                      : `linear-gradient(135deg, ${tplForm.accent}22 0%, ${tplForm.accent}66 50%, ${tplForm.accent}22 100%)`,
                    borderColor: tplForm.accent,
                  }}>
                  <div className="absolute inset-0 flex flex-col items-center justify-between p-[6%] text-center">
                    <div className="w-full flex justify-between text-[8px] font-bold uppercase tracking-widest" style={{ color: tplForm.bg_url ? tplForm.text_dark : tplForm.accent }}>
                      <span className="opacity-0">.</span>
                      <span className="text-right">
                        <div className="opacity-70">UNIQUE NO.</div>
                        <div className="text-[10px] font-black" style={{ color: tplForm.bg_url ? tplForm.text_dark : '#fff' }}>{(tplForm.unique_prefix || 'SAMADHAN').toUpperCase()}-A1B2</div>
                      </span>
                    </div>
                    <div>
                      <div className="text-[8px] font-bold tracking-wider" style={{ color: tplForm.bg_url ? tplForm.text_dark : tplForm.accent }}>Presented To:</div>
                      <div className="font-black tracking-wide" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(11px, 1.8vw, 20px)', color: tplForm.bg_url ? '#a16207' : '#fff', textShadow: tplForm.bg_url ? 'none' : '1px 1px 3px rgba(0,0,0,0.4)' }}>DEMO USER</div>
                    </div>
                    <div>
                      <div className="text-[9px]" style={{ color: tplForm.bg_url ? tplForm.text_dark : tplForm.accent }}>Contribution of</div>
                      <div className="font-black" style={{ fontSize: 'clamp(11px, 1.8vw, 20px)', color: tplForm.bg_url ? '#065f46' : '#10b981' }}>1,00,000 AT</div>
                    </div>
                    <div className="w-full flex justify-between items-end text-[7px]">
                      <div className="text-left opacity-70" style={{ color: tplForm.bg_url ? tplForm.text_dark : tplForm.accent }}>
                        <div>Cert ID: SC-2601-DEMO</div>
                        <div>Date: {new Date().toLocaleDateString('en-IN')}</div>
                      </div>
                      <div className="text-right" style={{ color: tplForm.bg_url ? tplForm.text_dark : '#fff' }}>
                        {tplForm.signature_url ? (
                          <img src={tplForm.signature_url} onError={(e) => { e.currentTarget.style.display = 'none'; }} alt="sig" className="h-4 md:h-6 inline-block" />
                        ) : (
                          <div className="italic" style={{ fontFamily: 'Brush Script MT, cursive', fontSize: 'clamp(11px, 1.5vw, 16px)', color: tplForm.bg_url ? '#1e40af' : '#fbbf24' }}>{tplForm.signature_name || 'Signature'}</div>
                        )}
                        <div className="uppercase tracking-widest opacity-80" style={{ fontSize: '6px' }}>{tplForm.signature_title || 'TITLE'}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-[9px] text-slate-400 italic">Preview updates live as you type. Actual certificate will be full-size.</div>
              </div>
            </div>
          </details>
        </div>

        {/* Gallery Upload */}
        <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-sm font-bold text-emerald-300">📸 Photo & Video Gallery ({gallery.length}) — Add unlimited images/videos</div>
            <button
              onClick={seedSamples}
              disabled={busy}
              data-testid="samadhan-gallery-seed-samples"
              className="text-[11px] px-3 py-1.5 rounded-lg bg-gradient-to-r from-fuchsia-500 to-amber-500 text-white font-black flex items-center gap-1.5 hover:brightness-110 disabled:opacity-50"
              title="Add 15 curated sample images across all zones"
            >
              <Sparkles className="w-3 h-3" /> Seed 15 Sample Images
            </button>
          </div>
          <div className="text-[10px] text-slate-400 mb-2">✅ Photo: jpg/png/webp URL &nbsp;·&nbsp; ✅ Video: mp4/webm URL ya YouTube link (auto-detect)</div>
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 mb-3">
            <input placeholder="Image/Video URL (mp4/webm/YouTube ok)" value={form.image_url.startsWith('data:') ? '(file uploaded)' : form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="samadhan-img-url" className="sm:col-span-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs" />
            <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs" />
            <input placeholder="Caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs" />
            <select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs">
              <option value="general" className="bg-slate-900">General</option>
              <option value="wellness" className="bg-slate-900">Wellness</option>
              <option value="entertainment" className="bg-slate-900">Entertainment</option>
              <option value="leisure" className="bg-slate-900">Leisure</option>
            </select>
            <button onClick={addImg} disabled={busy} data-testid="samadhan-img-add" className="py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center justify-center gap-1"><Plus className="w-3 h-3" /> {busy ? 'Saving…' : 'Add'}</button>
          </div>
          <div className="mb-3">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/40 text-cyan-200 text-xs font-bold cursor-pointer" data-testid="samadhan-file-upload-label">
              📤 Upload File (image ≤8MB · video ≤50MB, server-hosted)
              <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => uploadGalleryFile(e.target.files?.[0])} data-testid="samadhan-file-upload-input" />
            </label>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {gallery.map((im) => {
              const url = im.image_url || '';
              const isYT = /youtube\.com|youtu\.be/i.test(url);
              const isVid = /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(url) || /^data:video\//i.test(url) || /vimeo\.com/i.test(url);
              return (
                <div key={im.id} className="relative rounded-lg overflow-hidden border border-white/10">
                  {isYT ? (
                    <div className="w-full h-24 bg-slate-800 flex items-center justify-center relative">
                      <img
                        src={`https://img.youtube.com/vi/${(url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/) || [])[1] || ''}/hqdefault.jpg`}
                        alt="yt" className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-red-600/80 text-white text-xs font-black px-2 py-0.5 rounded">▶ YT</div>
                      </div>
                    </div>
                  ) : isVid ? (
                    <div className="relative w-full h-24 bg-slate-800">
                      <video src={url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black/70 text-white text-xs font-black px-2 py-0.5 rounded">▶ VIDEO</div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={url} alt={im.title || 'img'}
                      className="w-full h-24 object-cover bg-slate-800"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent && !parent.querySelector('.broken-img-badge')) {
                          const div = document.createElement('div');
                          div.className = 'broken-img-badge w-full h-24 flex flex-col items-center justify-center bg-red-950/50 border border-red-400/40 text-center';
                          div.innerHTML = '<div class="text-xl">⚠️</div><div class="text-[9px] font-black text-red-300 uppercase">Broken URL</div>';
                          parent.insertBefore(div, parent.firstChild);
                        }
                      }}
                    />
                  )}
                  <button onClick={() => delImg(im.id)} className="absolute top-1 right-1 p-1 bg-red-500/80 rounded text-white"><Trash2 className="w-3 h-3" /></button>
                  <div className="text-[9px] text-slate-400 p-1 truncate">{im.title || im.zone}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Transactions */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="text-sm font-bold text-fuchsia-300 mb-2">💸 Recent Transactions ({txns.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[720px]">
              <thead className="text-slate-400 uppercase text-[10px]"><tr>
                <th className="text-left py-1">Date</th><th className="text-left">User</th><th className="text-left">Pack</th>
                <th className="text-right">Amount</th><th className="text-right">Reservoir 50%</th><th className="text-right">Affiliate Network 30%</th><th className="text-right">Admin 20%</th><th className="text-left">Cert ID</th>
              </tr></thead>
              <tbody>
                {txns.length === 0 && <tr><td colSpan={8} className="text-center py-4 text-slate-500">Koi transaction nahi</td></tr>}
                {txns.map((t) => (
                  <tr key={t.id} className="border-t border-white/5">
                    <td className="py-1">{(t.created_at || '').slice(0, 10)}</td>
                    <td>{t.full_name || t.username}</td>
                    <td>{t.pack_label}</td>
                    <td className="text-right font-mono">{fmt(t.amount)}</td>
                    <td className="text-right font-mono text-emerald-300">{fmt(t.split?.reservoir)}</td>
                    <td className="text-right font-mono text-fuchsia-300">{fmt(t.split?.mlm)}</td>
                    <td className="text-right font-mono text-amber-300">{fmt(t.split?.admin)}</td>
                    <td className="font-mono text-[10px]">{t.cert_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const Card = ({ label, val, sub, cls }) => (
  <div className={`rounded-xl border bg-gradient-to-br p-3 ${cls}`}>
    <div className="text-[10px] uppercase font-bold opacity-80">{label}</div>
    <div className="text-xl font-extrabold mt-0.5">{val}</div>
    {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
  </div>
);
